// @ts-nocheck
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Context from '../../context/Context';
import { URL } from '../../service/Service';
import { editFormElement } from '../../context/forms/FormsAction';
import TooltipMouseFollow from '../TooltipMouseFollow';

const VALIDATION_OPTIONS = [
    {
        value: 'EMPTY',
        label: 'Sem validação',
        description: 'Aceita qualquer texto.',
    },
    {
        value: 'TEXT',
        label: 'Texto',
        description: 'Valida como texto simples.',
    },
    {
        value: 'NUMBER',
        label: 'Números',
        description: 'Aceita apenas valores numéricos.',
    },
    {
        value: 'EMAIL',
        label: 'Email',
        description: 'Valida formato de email.',
    },
    {
        value: 'EMAIL_PAYMENT',
        label: 'Email de informação de pagamento',
        description: 'Usado para envio de informação relacionada com pagamento.',
    },
    {
        value: 'URL',
        label: 'URL',
        description: 'Valida endereço web.',
    },
    {
        value: 'NIFPT',
        label: 'NIF português',
        description: 'Valida NIF nacional.',
    },
    {
        value: 'NIF',
        label: 'NIF internacional',
        description: 'Validação mais genérica.',
    },
    {
        value: 'PHONE',
        label: 'Contacto telefónico com indicativo',
        description: 'Ex.: +351 912 345 678.',
    },
];

const InputEditWizard = ({ elementInfo, userRole }) => {
    const { idform, idformelement } = useParams();
    const { dispatch } = useContext(Context);
    const navigate = useNavigate();
    const location = useLocation();

    const [label, setLabel] = useState(elementInfo?.label ?? '');
    const [required, setRequired] = useState(elementInfo?.required ?? false);
    const [placeholder, setPlaceholder] = useState(elementInfo?.placeholder ?? '');
    const [regex, setRegex] = useState(elementInfo?.regex ?? 'EMPTY');

    const [initialLabel, setInitialLabel] = useState(elementInfo?.label ?? '');
    const [initialRequired, setInitialRequired] = useState(elementInfo?.required ?? false);
    const [initialPlaceholder, setInitialPlaceholder] = useState(elementInfo?.placeholder ?? '');
    const [initialRegex, setInitialRegex] = useState(elementInfo?.regex ?? 'EMPTY');

    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const isAdmin = userRole === 'ADMIN' || location.pathname.startsWith('/admin/forms/');
    const baseFormPath = isAdmin ? `/admin/forms/${idform}` : `/forms/${idform}`;

    useEffect(() => {
        const nextLabel = elementInfo?.label ?? '';
        const nextRequired = elementInfo?.required ?? false;
        const nextPlaceholder = elementInfo?.placeholder ?? '';
        const nextRegex = elementInfo?.regex ?? 'EMPTY';

        setLabel(nextLabel);
        setRequired(nextRequired);
        setPlaceholder(nextPlaceholder);
        setRegex(nextRegex);

        setInitialLabel(nextLabel);
        setInitialRequired(nextRequired);
        setInitialPlaceholder(nextPlaceholder);
        setInitialRegex(nextRegex);
    }, [
        elementInfo?.label,
        elementInfo?.required,
        elementInfo?.placeholder,
        elementInfo?.regex,
    ]);

    const hasChanges = useMemo(() => {
        return (
            label !== initialLabel ||
            required !== initialRequired ||
            placeholder !== initialPlaceholder ||
            regex !== initialRegex
        );
    }, [
        label,
        required,
        placeholder,
        regex,
        initialLabel,
        initialRequired,
        initialPlaceholder,
        initialRegex,
    ]);

    const selectedValidation = useMemo(() => {
        return VALIDATION_OPTIONS.find((option) => option.value === regex);
    }, [regex]);

    const canSave = label.trim().length > 0 && !saving;

    const goBackToEditor = () => {
        navigate(`${baseFormPath}/formwizard/1`);
    };

    const patchElement = async () => {
        const url = `${URL}/api/forms/${idform}/formelements/${idformelement}`;

        const request = {
            method: 'PATCH',
            headers: {
                'Content-type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                label: label.trim(),
                placeholder: placeholder.trim(),
                required,
                regex: regex || 'EMPTY',
            }),
        };

        await Promise.resolve(editFormElement(url, request, dispatch));
    };

    const deleteElement = async () => {
        const url = `${URL}/api/forms/${idform}/formelements/${idformelement}`;

        const request = {
            method: 'DELETE',
            headers: {
                'Content-type': 'application/json',
                Accept: 'application/json',
            },
        };

        await Promise.resolve(editFormElement(url, request, dispatch));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setError('');
        setSuccess(false);

        if (!label.trim()) {
            setError('A legenda do campo é obrigatória.');
            return;
        }

        try {
            setSaving(true);

            await patchElement();

            setSuccess(true);

            setTimeout(() => {
                goBackToEditor();
            }, 750);
        } catch (err) {
            console.error(err);
            setError('Não foi possível guardar o campo. Tenta novamente.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = async () => {
        try {
            const wasCreatedEmpty =
                initialLabel === '' &&
                initialRequired === false &&
                initialPlaceholder === '' &&
                (initialRegex === 'EMPTY' || initialRegex === '' || initialRegex == null);

            if (wasCreatedEmpty) {
                await deleteElement();
            }

            goBackToEditor();
        } catch (err) {
            console.error(err);
            setError('Não foi possível cancelar a edição deste campo.');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 px-5 py-6 text-slate-900">
            <div className="mx-auto max-w-7xl">
                <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <button
                            type="button"
                            onClick={goBackToEditor}
                            className="text-sm font-semibold text-emerald-700 transition hover:text-emerald-900"
                        >
                            ← Voltar ao editor
                        </button>

                        <p className="mt-5 text-xs font-bold uppercase tracking-[0.24em] text-emerald-700">
                            Campo de texto curto
                        </p>

                        <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-950 md:text-4xl">
                            Editar campo de texto
                        </h1>

                        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-500">
                            Define a legenda, o texto de ajuda, a obrigatoriedade e a validação deste campo.
                        </p>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
                        >
                            Cancelar
                        </button>

                        <button
                            type="submit"
                            form="input-edit-wizard-form"
                            disabled={!canSave}
                            className="rounded-xl bg-green-cl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:bg-slate-400"
                        >
                            {saving ? 'A guardar...' : 'Guardar alterações'}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-semibold text-emerald-700">
                        Dados guardados com sucesso.
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
                    <form
                        id="input-edit-wizard-form"
                        onSubmit={handleSubmit}
                        className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
                    >
                        <div className="border-b border-slate-100 px-6 py-5">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                                Conteúdo
                            </p>

                            <h2 className="mt-1 text-lg font-bold text-slate-950">
                                Identificação do campo
                            </h2>

                            <p className="mt-1 text-sm text-slate-500">
                                Estes textos aparecem no formulário público e ajudam o utilizador a preencher corretamente.
                            </p>
                        </div>

                        <div className="space-y-6 px-6 py-6">
                            <div>
                                <div className="flex items-center justify-between gap-4">
                                    <label
                                        htmlFor="input-label"
                                        className="flex items-center gap-2 text-sm font-bold text-slate-800"
                                    >
                                        Legenda *
                                        <TooltipMouseFollow message="Texto que será exibido como título do campo no formulário." />
                                    </label>

                                    <span className="text-xs text-slate-400">
                                        {label.length}/120
                                    </span>
                                </div>

                                <input
                                    id="input-label"
                                    type="text"
                                    value={label}
                                    maxLength={120}
                                    onChange={(event) => setLabel(event.target.value)}
                                    placeholder="Ex.: Nome completo"
                                    className="mt-2 block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                                    autoFocus
                                />

                                <p className="mt-2 text-xs text-slate-500">
                                    Obrigatório. Deve explicar claramente que dado estás a pedir.
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center justify-between gap-4">
                                    <label
                                        htmlFor="input-placeholder"
                                        className="flex items-center gap-2 text-sm font-bold text-slate-800"
                                    >
                                        Rótulo / placeholder
                                        <TooltipMouseFollow message="Texto sugestivo que aparece dentro do campo antes de o utilizador escrever." />
                                    </label>

                                    <span className="text-xs text-slate-400">
                                        {placeholder.length}/160
                                    </span>
                                </div>

                                <input
                                    id="input-placeholder"
                                    type="text"
                                    value={placeholder}
                                    maxLength={160}
                                    onChange={(event) => setPlaceholder(event.target.value)}
                                    placeholder="Ex.: Escreve aqui o teu nome"
                                    className="mt-2 block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                                />

                                <p className="mt-2 text-xs text-slate-500">
                                    Opcional. Ajuda o utilizador com um exemplo de preenchimento.
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-slate-100 px-6 py-5">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                                Validação
                            </p>

                            <h2 className="mt-1 text-lg font-bold text-slate-950">
                                Regras de preenchimento
                            </h2>

                            <p className="mt-1 text-sm text-slate-500">
                                Controla se o campo é obrigatório e que tipo de dado pode ser introduzido.
                            </p>
                        </div>

                        <div className="space-y-6 px-6 py-6">
                            <div>
                                <label
                                    htmlFor="validation-type"
                                    className="flex items-center gap-2 text-sm font-bold text-slate-800"
                                >
                                    Tipo de validação
                                    <TooltipMouseFollow message="Escolhe como o campo deve validar os dados introduzidos, por exemplo email, número ou NIF." />
                                </label>

                                <select
                                    id="validation-type"
                                    value={regex || 'EMPTY'}
                                    onChange={(event) => setRegex(event.target.value)}
                                    className="mt-2 block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                                >
                                    {VALIDATION_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>

                                {selectedValidation && (
                                    <p className="mt-2 text-xs text-slate-500">
                                        {selectedValidation.description}
                                    </p>
                                )}
                            </div>

                            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                                <label
                                    htmlFor="input-required"
                                    className="flex cursor-pointer items-start justify-between gap-4"
                                >
                                    <div>
                                        <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
                                            Campo obrigatório
                                            <TooltipMouseFollow message="Se ativo, este campo deve obrigatoriamente ser preenchido pelo utilizador." />
                                        </span>

                                        <p className="mt-1 text-xs leading-5 text-slate-500">
                                            Ativa esta opção quando a resposta for necessária para concluir o formulário.
                                        </p>
                                    </div>

                                    <input
                                        id="input-required"
                                        type="checkbox"
                                        checked={!!required}
                                        onChange={(event) => setRequired(event.target.checked)}
                                        className="mt-1 h-5 w-5 cursor-pointer rounded border-slate-300 text-green-cl focus:ring-green-cl"
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-xs text-slate-500">
                                {hasChanges ? (
                                    <span className="font-semibold text-amber-700">
                                        Existem alterações por guardar.
                                    </span>
                                ) : (
                                    <span>
                                        Sem alterações pendentes.
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col gap-2 sm:flex-row">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    disabled={!canSave}
                                    className="rounded-xl bg-green-cl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:bg-slate-400"
                                >
                                    {saving ? 'A guardar...' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    </form>

                    <aside className="space-y-5">
                        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">
                                Pré-visualização
                            </p>

                            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-6">
                                <label className="block text-sm font-bold text-slate-800">
                                    {label.trim() || 'Legenda do campo'}
                                    {required && <span className="ml-1 text-red-500">*</span>}
                                </label>

                                <input
                                    type="text"
                                    disabled
                                    placeholder={placeholder.trim() || 'Texto de exemplo dentro do campo'}
                                    className="mt-2 block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-500"
                                />

                                <p className="mt-3 text-xs text-slate-500">
                                    Validação: <span className="font-semibold">{selectedValidation?.label ?? 'Sem validação'}</span>
                                </p>
                            </div>
                        </section>

                        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="font-bold text-slate-950">
                                Boas práticas
                            </h3>

                            <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                                <li className="rounded-2xl bg-slate-50 px-4 py-3">
                                    Usa uma legenda curta, como “Nome”, “Email” ou “Telefone”.
                                </li>

                                <li className="rounded-2xl bg-slate-50 px-4 py-3">
                                    Escolhe validação quando queres evitar respostas inválidas.
                                </li>

                                <li className="rounded-2xl bg-slate-50 px-4 py-3">
                                    Marca como obrigatório apenas o que for mesmo necessário.
                                </li>
                            </ul>
                        </section>

                        <section className="rounded-3xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
                            <h3 className="font-bold text-amber-900">
                                Nota
                            </h3>

                            <p className="mt-2 text-sm leading-6 text-amber-800">
                                Se cancelares um campo recém-criado sem preencher nada, ele será removido automaticamente.
                            </p>
                        </section>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default InputEditWizard;